import React from "react";
import classNames from "classnames";
import _get from "lodash-es/get";
import _isEmpty from "lodash-es/isEmpty";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import Checkbox from "antd/es/checkbox";

import { ADD_FORM } from "src/ipm-shared/store/model/CollectedAccount/const";
import FileUtils from "src/ipm-shared/Utils/Files";

import Form from "src/bepaid/components/Form";
import InputFile from "src/bepaid/components/Form/controls/InputFile";
import InputText from "src/bepaid/components/Form/controls/InputText";
import InputFileDropZone from "src/bepaid/components/Form/controls/InputFile/components/InputFileDropZone";
import IconUpload from "src/bepaid/assets/images/common/icon_upload.svg";
import Image from "src/bepaid/components/Image";
import IconDelete from "src/bepaid/assets/images/common/icon_delete.svg";
import FormStyles from "src/bepaid/components/Form/CommonForm.module.scss";

import OtherStyles from "./AccountDetails.module.scss";
import { COUNTRY_CODES } from "src/bepaid/layouts/Menu/components/Companies/const";
import SignaturePad from "src/bepaid/components/Form/controls/SignaturePad";

const styles = {
  ...FormStyles,
  ...OtherStyles
};
const AccountDetailsForm = (props: any) => {
  const commonFormItemProps = {
    reserveValueOnUnmount: true,
    revertValueOnMount: true
  };
  const bDigitalSignatureOptions = {
    file: "file",
    handWritten: "hand_written"
  };

  const data = props.collectedAccount;

  const initialShareholders = React.useMemo(() => {
    const existing = _get(data, "payeeData.kycDocuments.b_shareholders", null);
    if (Array.isArray(existing) && existing.length > 0) return existing;
    return [
      {
        name: _get(data, "payeeData.kycDocuments.b_registered_name", ""),
        address: _get(data, "payeeData.kycDocuments.b_registered_address", ""),
        b_shareholders: _get(data, "payeeData.kycDocuments.b_shareholder", []),
        b_shareholder_poa: _get(
          data,
          "payeeData.kycDocuments.b_shareholder_poa",
          []
        )
      }
    ];
  }, []);

  const [shareholderIds, setShareholderIds] = React.useState<number[]>(() =>
    Array.from({ length: initialShareholders.length }, (_, i) => i)
  );
  const nextIdRef = React.useRef(initialShareholders.length);

  React.useEffect(() => {
    props.setControl({
      form: ADD_FORM,
      name: "b_shareholder_ids",
      value: shareholderIds.join(",")
    });
  }, [shareholderIds]);

  const addShareholder = () => {
    const id = nextIdRef.current++;
    setShareholderIds(prev => [...prev, id]);
  };

  const removeShareholder = (id: number) =>
    setShareholderIds(prev => prev.filter(sid => sid !== id));

  const [valueOption, setValueOption] = React.useState({
    bPoba: _get(props.optionAccountDetail, "bPoba", "N"),
    bStructuredChart: _get(props.optionAccountDetail, "bStructuredChart", "N"),
    bLetterOfAuthorization: _get(
      props.optionAccountDetail,
      "bLetterOfAuthorization",
      "N"
    ),
    bDirector: _get(props.optionAccountDetail, "bDirector", "N"),
    bShareholder: _get(props.optionAccountDetail, "bShareholder", "N"),
    bShareholderCorporate: _get(
      props.optionAccountDetail,
      "bShareholderCorporate",
      "N"
    ),
    bDigitalSignature: _get(
      props.optionAccountDetail,
      "bDigitalSignature",
      bDigitalSignatureOptions.handWritten
    )
  });
  const onChangeOption = (option: string) => (e: any) => {
    props.setOptionAccountDetail(option, e.target.value);
    setValueOption({
      ...valueOption,
      [option]: e.target.value
    });
    if (option === "bShareholder") {
      setShareholderIds([0]);
      nextIdRef.current = 1;
    }
  };
  const onChangeCheckBox = (e: any) => {
    props.setCheckboxConfirm({
      [e.target.name]: e.target.checked
    });
  };

  const isSG = props.accountCountryCode === "SG";

  const objByCountry = {
    [COUNTRY_CODES.SINGAPORE]: {
      documentName: "ACRA Bizfile",
      documentRequired:
        "Your ACRA Bizfile needs to be dated within the last 3 months.",
      documentUploadLabel: "Upload ACRA Bizfile",
      governmentRegistration: "ACRA Bizfile",
      officalCompanySummary: "ACRA Bizfile or equivalent Company Extract",
      addressProof: "NRIC (front and back) / National IDs or Passport"
    },
    [COUNTRY_CODES.HONGKONG]: {
      documentName: "Official Annual Return document (Form NAR1)",
      documentRequired:
        "Your document needs to be dated within the last 3 months.",
      documentUploadLabel: "Upload document",
      documentSampleUrl:
        "https://s3.ap-southeast-1.amazonaws.com/ipaymy.com/docs/HK+Annual+Return.pdf",
      governmentRegistration: "Official Current Company Extract",
      officalCompanySummary: "Form NAR1 or equivalent Company Extract",
      addressProof: "NRIC (front and back) / National IDs or Passport"
    },
    [COUNTRY_CODES.MALAYSIA]: {
      documentName: "Official Company Profile document",
      documentRequired:
        "Your document needs to be dated within the last 3 months.",
      documentUploadLabel: "Upload document",
      documentSampleUrl:
        "https://s3.ap-southeast-1.amazonaws.com/ipaymy.com/docs/MY+Company+Profile.pdf",
      governmentRegistration: "Official Current Company Extract",
      officalCompanySummary: "SSM Extract or equivalent Company Extract",
      addressProof: "NRIC (front and back) / National IDs or Passport"
    },
    [COUNTRY_CODES.AUSTRALIA]: {
      documentName: "Official Current Company Extract document",
      documentRequired:
        "Your document needs to be dated within the last 3 months.",
      documentUploadLabel: "Upload document",
      documentSampleUrl:
        "https://s3.ap-southeast-1.amazonaws.com/ipaymy.com/docs/AU+Current+Company+Extract.pdf",
      governmentRegistration: "Official Current Company Extract",
      officalCompanySummary: "ASIC Extract or equivalent Company Extract",
      addressProof: "National IDs/Passport/Driving Licence"
    }
  };
  return (
    <div className={styles.content}>
      <Form
        layout="vertical"
        requiredMark={false}
        className={styles.personalFormDetail}
      >
        <p className={styles.label}>
          {objByCountry[props.accountCountryCode].documentName}
        </p>
        <p
          className={classNames(
            styles.text,
            styles.purple,
            styles.textSm,
            styles.textDescription
          )}
        >
          {objByCountry[props.accountCountryCode].documentRequired}
        </p>
        <InputFile
          className="upload_bank_documents"
          defaultValue={
            _get(data, "payeeData.kycDocuments.b_acra_bizfile", [])
              ? FileUtils.getInfoFromKeys(
                  _get(data, "payeeData.kycDocuments.b_acra_bizfile", [])
                )
              : []
          }
          allowedFileTypes={["jpg", "png", "heic", "pdf"]}
          name="b_acra_bizfile"
          purpose="kyc"
          label={objByCountry[props.accountCountryCode].documentUploadLabel}
          form={ADD_FORM}
          maxFileAmount={1}
          helperText={{
            showInEmptyMode: true,
            txt: (
              <>
                <div className={classNames(styles.documentHelperText)}>
                  Maximum file size: <b>16 MB</b>. Supported file types:{" "}
                  <b>JPG, PDF, PNG</b>.
                </div>
                {!isSG && (
                  <div>
                    Please refer{" "}
                    <a
                      href={
                        objByCountry[props.accountCountryCode].documentSampleUrl
                      }
                      target="_blank"
                    >
                      here
                    </a>{" "}
                    for a sample reference of the document requested.
                  </div>
                )}
              </>
            )
          }}
          {...commonFormItemProps}
        />
      </Form>
      <Form
        layout="vertical"
        requiredMark={false}
        className={styles.personalFormDetail}
      >
        <p className={styles.label}>
          Is your principal place of business different from the address on your{" "}
          {objByCountry[props.accountCountryCode].governmentRegistration}?
        </p>
        <div className={styles.wrapperRadioGroup}>
          <Radio.Group
            onChange={onChangeOption("bPoba")}
            defaultValue={"N"}
            value={valueOption.bPoba}
          >
            <Space direction="horizontal">
              <Radio value={"Y"}>Yes</Radio>
              <Radio value={"N"}>No</Radio>
            </Space>
          </Radio.Group>
        </div>

        {valueOption.bPoba === "Y" && (
          <>
            <p
              className={classNames(
                styles.text,
                styles.purple,
                styles.textSm,
                styles.textDescription
              )}
            >
              Please provide a proof of address, such as a utility bill or bank
              statement dated within the last 3 months, of your principal place
              of business.
            </p>
            <InputFile
              className="upload_bank_documents"
              defaultValue={
                _get(data, "payeeData.kycDocuments.b_poba", [])
                  ? FileUtils.getInfoFromKeys(
                      _get(data, "payeeData.kycDocuments.b_poba", [])
                    )
                  : []
              }
              allowedFileTypes={["jpg", "png", "heic", "pdf"]}
              name="b_poba"
              purpose="kyc"
              label="Upload proof of address"
              form={ADD_FORM}
              maxFileAmount={1}
              helperText={{
                showInEmptyMode: true,
                txt: (
                  <span>
                    Maximum file size: <b>16 MB</b>. Supported file types:{" "}
                    <b>JPG, PDF, PNG</b>.
                  </span>
                )
              }}
              {...commonFormItemProps}
            />
          </>
        )}
      </Form>
      <Form
        layout="vertical"
        requiredMark={false}
        className={styles.personalFormDetail}
      >
        <p className={styles.label}>
          Do you have any individual shareholders who own 25% or more in your
          business?
        </p>
        <div className={styles.wrapperRadioGroup}>
          <Radio.Group
            onChange={onChangeOption("bShareholder")}
            defaultValue={"N"}
            value={valueOption.bShareholder}
          >
            <Space direction="horizontal">
              <Radio value={"Y"}>Yes</Radio>
              <Radio value={"N"}>No</Radio>
            </Space>
          </Radio.Group>
        </div>

        {valueOption.bShareholder === "Y" && (
          <>
            <p
              className={classNames(
                styles.text,
                styles.purple,
                styles.textSm,
                styles.textDescription
              )}
            >
              Please input the full name and residential address of{" "}
              <span className={styles.boldUnderlineText}>all</span> individual
              shareholders who ultimately own 25% or more shares (directly or
              indirectly) or{" "}
              <span className={styles.boldUnderlineText}>all</span> individuals
              who exercise effective control of your company.
            </p>
            <p
              className={classNames(
                styles.text,
                styles.purple,
                styles.textSm,
                styles.textDescription
              )}
            >
              If more than one person, click the “+Add” below to automatically
              add another entry!
            </p>

            {shareholderIds.map((id, position) => (
              <div key={id} className={styles.shareholderEntry}>
                <div className={styles.shareholderHeader}>
                  <label>Full name</label>
                  {position > 0 && (
                    <Image
                      src={IconDelete}
                      className={styles.deleteIcon}
                      onClick={() => removeShareholder(id)}
                    />
                  )}
                </div>
                <InputText
                  form={ADD_FORM}
                  name={`b_registered_name_${id}`}
                  placeholder="Full name"
                  defaultValue={initialShareholders[position]?.name || ""}
                />
                <InputText
                  form={ADD_FORM}
                  name={`b_registered_address_${id}`}
                  placeholder="Residential Address"
                  label="Residential Address"
                  defaultValue={initialShareholders[position]?.address || ""}
                />
                <p
                  className={classNames(
                    styles.text,
                    styles.purple,
                    styles.textSm,
                    styles.textDescription
                  )}
                >
                  Please provide a copy of the{" "}
                  {objByCountry[props.accountCountryCode].addressProof} of the
                  listed individuals
                </p>
                <InputFile
                  className="upload_bank_documents"
                  btnClassName={styles.uploadBtn}
                  defaultValue={
                    initialShareholders[position]?.b_shareholders
                      ? FileUtils.getInfoFromKeys(
                          initialShareholders[position].b_shareholders
                        )
                      : []
                  }
                  allowedFileTypes={["jpg", "png", "heic", "pdf"]}
                  name={`b_shareholder_${id}`}
                  purpose="kyc"
                  label="Upload the National IDs"
                  form={ADD_FORM}
                  canDrag={true}
                  multiple={true}
                  maxFileAmount={10}
                  renderComponent={InputFileDropZone}
                  {...commonFormItemProps}
                />
                <p
                  className={classNames(
                    styles.text,
                    styles.purple,
                    styles.textSm,
                    styles.textDescription
                  )}
                >
                  Please provide a copy of a proof of address (utility bill,
                  bank statement or other document) no later than three months
                  old of the listed individuals
                </p>
                <InputFile
                  className="upload_bank_documents"
                  btnClassName={styles.uploadBtn}
                  defaultValue={
                    initialShareholders[position]?.b_shareholder_poa
                      ? FileUtils.getInfoFromKeys(
                          initialShareholders[position].b_shareholder_poa
                        )
                      : []
                  }
                  allowedFileTypes={["jpg", "png", "heic", "pdf"]}
                  name={`b_shareholder_poa_${id}`}
                  purpose="kyc"
                  label="Upload the Proof of Address"
                  form={ADD_FORM}
                  canDrag={true}
                  multiple={true}
                  maxFileAmount={10}
                  renderComponent={InputFileDropZone}
                  {...commonFormItemProps}
                />
              </div>
            ))}
            <p>
              Need to add another person? Click{" "}
              <a className={styles.addShareholder} onClick={addShareholder}>
                + Add
              </a>
            </p>
          </>
        )}
      </Form>
      {
        <Form
          layout="vertical"
          requiredMark={false}
          className={styles.personalFormDetail}
        >
          <p className={styles.label}>
            Do you have at least one corporate shareholder with a stake of 25%
            or more in your business?
          </p>
          <div className={styles.wrapperRadioGroup}>
            <Radio.Group
              onChange={onChangeOption("bStructuredChart")}
              defaultValue={"N"}
              value={valueOption.bStructuredChart}
            >
              <Space direction="horizontal">
                <Radio value={"Y"}>Yes</Radio>
                <Radio value={"N"}>No</Radio>
              </Space>
            </Radio.Group>
          </div>

          {valueOption.bStructuredChart === "Y" && (
            <>
              <p
                className={classNames(
                  styles.text,
                  styles.purple,
                  styles.textSm,
                  styles.textDescription
                )}
              >
                Please provide an organisational chart showing the ownership of
                your Company. This chart should include all intermediate
                companies up to the ultimate beneficial owners - individuals who
                directly or indirectly own 25% or more of the Company.
              </p>
              <InputFile
                className="upload_bank_documents"
                defaultValue={
                  _get(data, "payeeData.kycDocuments.b_structured_chart", [])
                    ? FileUtils.getInfoFromKeys(
                        _get(
                          data,
                          "payeeData.kycDocuments.b_structured_chart",
                          []
                        )
                      )
                    : []
                }
                allowedFileTypes={["jpg", "png", "heic", "pdf"]}
                name="b_structured_chart"
                purpose="kyc"
                label="Upload ultimate beneficial ownership chart"
                form={ADD_FORM}
                maxFileAmount={1}
                helperText={{
                  showInEmptyMode: true,
                  txt: (
                    <span>
                      Maximum file size: <b>16 MB</b>. Supported file types:{" "}
                      <b>JPG, PDF, PNG</b>.
                    </span>
                  )
                }}
                {...commonFormItemProps}
              />
            </>
          )}
        </Form>
      }
      {
        <Form
          layout="vertical"
          requiredMark={false}
          className={styles.personalFormDetail}
        >
          <p className={styles.label}>
            Do you have any corporate shareholders who own 25% or more in your
            business?
          </p>
          <div className={styles.wrapperRadioGroup}>
            <Radio.Group
              onChange={onChangeOption("bShareholderCorporate")}
              defaultValue={"N"}
              value={valueOption.bShareholderCorporate}
            >
              <Space direction="horizontal">
                <Radio value={"Y"}>Yes</Radio>
                <Radio value={"N"}>No</Radio>
              </Space>
            </Radio.Group>
          </div>

          {valueOption.bShareholderCorporate === "Y" && (
            <>
              <p
                className={classNames(
                  styles.text,
                  styles.purple,
                  styles.textSm,
                  styles.textDescription
                )}
              >
                Please provide a copy of the{" "}
                {objByCountry[props.accountCountryCode].officalCompanySummary}{" "}
                of <span className={styles.boldUnderlineText}>all</span>{" "}
                corporate shareholders who hold more than 25% of the shares of
                the company.
              </p>
              <p
                className={classNames(
                  styles.text,
                  styles.purple,
                  styles.textSm,
                  styles.textDescription
                )}
              >
                We would still require you to provide the ultimate beneficial
                owner (individuals who ultimately own 25% or more shares
                directly or indirectly) or persons who exercise effective
                control of your company as stated in the previous section!
              </p>
              <InputFile
                className="upload_bank_documents"
                btnClassName={styles.uploadBtn}
                defaultValue={
                  _get(
                    data,
                    "payeeData.kycDocuments.b_shareholder_corporate",
                    []
                  )
                    ? FileUtils.getInfoFromKeys(
                        _get(
                          data,
                          "payeeData.kycDocuments.b_shareholder_corporate",
                          []
                        )
                      )
                    : []
                }
                allowedFileTypes={["jpg", "png", "heic", "pdf"]}
                name="b_shareholder_corporate"
                purpose="kyc"
                label="Upload the ACRA BizFile for all corporate shareholders"
                form={ADD_FORM}
                canDrag={true}
                multiple={true}
                maxFileAmount={10}
                renderComponent={InputFileDropZone}
                {...commonFormItemProps}
              />
            </>
          )}
        </Form>
      }
      <Form
        layout="vertical"
        requiredMark={false}
        className={styles.personalFormDetail}
      >
        <p className={styles.label}>
          Are there any non-directors, employees, or other persons who will use
          ipaymy on the company's behalf?
        </p>
        <div className={styles.wrapperRadioGroup}>
          <Radio.Group
            onChange={onChangeOption("bLetterOfAuthorization")}
            defaultValue={"N"}
            value={valueOption.bLetterOfAuthorization}
          >
            <Space direction="horizontal">
              <Radio value={"Y"}>Yes</Radio>
              <Radio value={"N"}>No</Radio>
            </Space>
          </Radio.Group>
        </div>

        {valueOption.bLetterOfAuthorization === "Y" && (
          <>
            <p
              className={classNames(
                styles.text,
                styles.purple,
                styles.textSm,
                styles.textDescription
              )}
            >
              Please upload the following documents for persons authorised to
              act (NP2A) on behalf of your company:
              <ul>
                <li>
                  <a
                    href={
                      "https://s3-ap-southeast-1.amazonaws.com/ipaymy.com/docs/ipaymy+LOA+Template.docx"
                    }
                    target="_blank"
                    className={styles.linkText}
                    rel="noopener noreferrer"
                  >
                    <b>Letter of Authorisation</b>
                  </a>{" "}
                  signed by the Director which should cover all individuals
                  (including directors) who will use ipaymy on behalf of your
                  Company.
                </li>
                <li>
                  <b>National ID/Passport copies</b> of{" "}
                  <b>
                    <u>all</u>
                  </b>{" "}
                  individuals (including Directors) who are acting on behalf of
                  the Company.
                </li>
              </ul>
            </p>
            <InputFile
              className="upload_bank_documents"
              defaultValue={
                _get(
                  data,
                  "payeeData.kycDocuments.b_letter_of_authorization",
                  []
                )
                  ? FileUtils.getInfoFromKeys(
                      _get(
                        data,
                        "payeeData.kycDocuments.b_letter_of_authorization",
                        []
                      )
                    )
                  : []
              }
              allowedFileTypes={["jpg", "png", "heic", "pdf"]}
              name="b_letter_of_authorization"
              purpose="kyc"
              label="Upload letter of authorisation"
              form={ADD_FORM}
              canDrag={true}
              multiple={true}
              maxFileAmount={10}
              renderComponent={InputFileDropZone}
              {...commonFormItemProps}
            />
          </>
        )}
      </Form>
      <div className={styles.wrapperRadioGroup}>
        <Radio.Group
          onChange={onChangeOption("bDigitalSignature")}
          defaultValue={bDigitalSignatureOptions.handWritten}
          value={valueOption.bDigitalSignature}
        >
          <Space direction="horizontal">
            <Radio value={bDigitalSignatureOptions.handWritten}>
              Handwritten signature
            </Radio>
            <Radio value={bDigitalSignatureOptions.file}>
              Upload digital signature
            </Radio>
          </Space>
        </Radio.Group>
      </div>
      {valueOption.bDigitalSignature === bDigitalSignatureOptions.file ? (
        <Form
          layout="vertical"
          requiredMark={false}
          className={styles.personalFormDetail}
        >
          <p className={styles.label}>
            Alternatively, please upload a clear and focus digital signature for
            record purpose.
          </p>
          <p
            className={classNames(
              styles.text,
              styles.textSm,
              styles.purple,
              styles.textDescription
            )}
          >
            Please ensure the details on your digital signature must be clearly
            visible.
          </p>
          <InputFile
            allowedFileTypes={["jpg", "jpeg", "png", "heic"]}
            name={"b_digital_signature"}
            purpose={"kyc"}
            form={ADD_FORM}
            label={"Upload your front ID photo"}
            icon={IconUpload}
            canDrag={true}
            multiple={true}
            renderComponent={InputFileDropZone}
            defaultValue={[]}
          />
        </Form>
      ) : (
        <Form
          layout="vertical"
          requiredMark={false}
          className={styles.personalFormDetail}
        >
          <p className={styles.label}>Please sign below for record purpose.</p>
          <SignaturePad
            form={ADD_FORM}
            name={"b_digital_signature"}
            purpose={"kyc"}
          />
        </Form>
      )}
      <Checkbox
        className={styles.checkbox}
        name={"checkboxOther"}
        onChange={onChangeCheckBox}
      >
        By registering with us, you confirm that you will not use ipaymy or our
        Services for any purpose related to{" "}
        <a
          className={styles.restrictedText}
          href="https://help.ipaymy.com/en/articles/3268073-businesses-restricted-from-payment-collection"
          target="_blank"
        >
          Restricted Businesses
        </a>{" "}
        or any illegal activities, including making, receiving, facilitating, or
        processing payments.
      </Checkbox>
      {props.checkboxConfirm.checkboxOtherError && (
        <p className={styles.checkboxError}>
          {props.checkboxConfirm.checkboxOtherError}
        </p>
      )}
    </div>
  );
};
export default AccountDetailsForm;
